import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { startVersionPoller } from "@/lib/versionCheck";

/**
 * Fixed bottom-right banner that appears once the app detects a newer
 * build has been deployed. Users click Reload to pick up the fresh
 * bundle without doing a manual hard refresh. Dismiss (X) silences it
 * for the session — the banner will NOT re-appear until the tab is
 * closed and reopened, so we never nag mid-task.
 */
export default function VersionUpdateBanner() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stop = startVersionPoller(() => setHasUpdate(true));
    return stop;
  }, []);

  if (!hasUpdate || dismissed) return null;

  const reload = () => {
    // `location.reload()` will re-request index.html; with the no-cache
    // meta tags + cache-busted /version.json, the browser will pick up
    // the fresh hashed bundles immediately.
    window.location.reload();
  };

  return (
    <div
      data-testid="version-update-banner"
      className="fixed bottom-5 right-5 z-[9999] max-w-sm rounded-xl border border-emerald-500/30 bg-[#0f1a14] shadow-2xl shadow-black/40 backdrop-blur-sm"
    >
      <div className="flex items-start gap-3 px-4 py-3.5">
        <div className="mt-0.5 shrink-0 rounded-lg bg-emerald-500/15 p-1.5">
          <RefreshCw className="h-4 w-4 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">
            A new version is available
          </div>
          <div className="mt-0.5 text-xs text-[#A1A9B8]">
            Reload to get the latest updates.
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={reload}
              data-testid="version-update-reload-btn"
              className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-400 transition-colors"
            >
              Reload now
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              data-testid="version-update-dismiss-btn"
              className="rounded-md px-2.5 py-1.5 text-xs font-medium text-[#A1A9B8] hover:text-white hover:bg-white/5 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1 text-[#6E7787] hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
